/**
 * BillDetectiveCard — small dashboard chiclet for Engine #29.
 *
 * Renders a 1-row hero strip: "Top saver this week / Top spender this week"
 * with a CTA to the full /bill-detective page. Self-hides cleanly if the
 * summary API errors (logged-out user, network issue) so the dashboard
 * never breaks.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Search, Trophy, AlertTriangle, ArrowRight, Sparkles, Cloud } from 'lucide-react';
import InlineAWSConnect from './InlineAWSConnect';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const fmtUsd = (n) => {
  const v = Math.abs(Number(n || 0));
  if (v >= 1000) return `$${(v / 1000).toFixed(1)}K`;
  if (v >= 100)  return `$${v.toFixed(0)}`;
  return `$${v.toFixed(2)}`;
};

export default function BillDetectiveCard() {
  const [summary, setSummary] = useState(null);
  const [errored, setErrored] = useState(false);
  const [awsOpen, setAwsOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/bill-detective/summary`, auth())
      .then((r) => { if (alive) setSummary(r.data); })
      .catch(() => { if (alive) setErrored(true); });
    return () => { alive = false; };
  }, []);

  if (errored || !summary) return null;
  // Guard: don't render an empty card if there's literally nothing to show
  if (!summary.top_saver_name && !summary.top_spender_name) return null;

  return (
    <Card
      className="relative overflow-hidden bg-gradient-to-br from-slate-900/80 via-slate-900/60 to-emerald-900/20 ring-1 ring-emerald-500/30 mb-6"
      data-testid="dashboard-bill-detective-card"
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <div className="flex items-center gap-2 text-xs">
              <Search className="w-3.5 h-3.5 text-emerald-300" />
              <span className="text-emerald-300 font-medium tracking-wide uppercase">
                Bill Detective
              </span>
              <span className="bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/30 text-[10px] px-2 py-0.5 rounded-full">
                <Sparkles className="w-2.5 h-2.5 inline mr-0.5" /> Bill Detective · NEW
              </span>
            </div>
            <h3 className="mt-2 text-lg font-medium text-slate-100">
              Who spent money this week?
            </h3>

            <div className="mt-3 grid sm:grid-cols-2 gap-4">
              {summary.top_saver_name && (
                <div className="flex items-center gap-3" data-testid="bd-card-top-saver">
                  <div className="w-9 h-9 rounded-full bg-emerald-500/15 ring-1 ring-emerald-500/40 flex items-center justify-center">
                    <Trophy className="w-4 h-4 text-emerald-300" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs text-slate-400">Top saver</div>
                    <div className="text-sm text-slate-100 truncate">{summary.top_saver_name}</div>
                    <div className="text-xs text-emerald-300 font-mono">
                      −{fmtUsd(summary.top_saver_saved_usd)}/mo
                    </div>
                  </div>
                </div>
              )}
              {summary.top_spender_name && (
                <div className="flex items-center gap-3" data-testid="bd-card-top-spender">
                  <div className="w-9 h-9 rounded-full bg-amber-500/15 ring-1 ring-amber-500/40 flex items-center justify-center">
                    <AlertTriangle className="w-4 h-4 text-amber-300" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs text-slate-400">Top spender</div>
                    <div className="text-sm text-slate-100 truncate">{summary.top_spender_name}</div>
                    <div className="text-xs text-amber-300 font-mono">
                      +{fmtUsd(summary.top_spender_spent_usd)}/mo
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <Link
            to="/bill-detective"
            className="inline-flex items-center gap-2 text-sm font-medium text-emerald-200 hover:text-emerald-100 bg-emerald-500/10 hover:bg-emerald-500/20 ring-1 ring-emerald-500/30 rounded-full px-4 py-2 transition"
            data-testid="dashboard-bill-detective-cta"
          >
            Open Bill Detective <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="mt-4 pt-4 border-t border-slate-800 flex items-center justify-between flex-wrap gap-2">
          <span className="text-[11px] text-slate-500">
            Connect AWS to replace demo data with your real engineers + commits.
          </span>
          <button
            type="button"
            onClick={() => setAwsOpen(true)}
            className="inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-emerald-200 bg-slate-800/50 hover:bg-slate-800 ring-1 ring-slate-700 hover:ring-emerald-500/40 rounded-full px-3 py-1 transition"
            data-testid="dashboard-reconnect-aws"
          >
            <Cloud className="w-3 h-3" /> Connect / Re-connect AWS
          </button>
        </div>
      </CardContent>

      <InlineAWSConnect
        open={awsOpen}
        onClose={() => setAwsOpen(false)}
        onConnected={() => setAwsOpen(false)}
      />
    </Card>
  );
}
